import { useEffect, useRef, useState } from "react";

export interface UseDebounceValueOptions<T> {
  equalityFn?: (left: T, right: T) => boolean;
}

/**
 * Returns a debounced version of the provided value that only updates
 * after the specified delay has elapsed without further changes.
 *
 * Unlike the `usehooks-ts` implementation this replaces, cleanup is
 * handled by a single `useEffect` — so the pending timer is always
 * cancelled when the component unmounts or when the value changes.
 *
 * @param value - The value to debounce (or a factory that returns it).
 *   **Caveat:** factories that return a new object reference every render will
 *   defeat the default `===` equality check, causing a timer to be scheduled on
 *   every render. Callers should either stabilize the value (e.g. `useMemo`) or
 *   supply a custom `equalityFn`.
 * @param delay - Debounce delay in milliseconds.
 * @param options - Optional settings.
 * @param options.equalityFn - Custom equality check; when it returns `true`
 *   the debounced value is **not** scheduled for update.
 * @returns A `[debouncedValue]` tuple (second element omitted for simplicity;
 *   all existing call-sites only destructure the first element).
 */
function unwrap<T>(v: T | (() => T)): T {
  return typeof v === "function" ? (v as () => T)() : v;
}

export function useDebounceValue<T>(value: T | (() => T), delay: number, options?: UseDebounceValueOptions<T>): [T] {
  const [debouncedValue, setDebouncedValue] = useState(() => unwrap(value));
  const previousValueRef = useRef(debouncedValue);

  const eqFn = options?.equalityFn ?? ((a: T, b: T) => a === b);
  const eqRef = useRef(eqFn);
  eqRef.current = eqFn;

  const currentValue = unwrap(value);

  useEffect(() => {
    if (eqRef.current(previousValueRef.current, currentValue)) {
      return;
    }

    const timer = setTimeout(() => {
      previousValueRef.current = currentValue;
      setDebouncedValue(currentValue);
    }, delay);

    return () => clearTimeout(timer);
  }, [currentValue, delay]);

  return [debouncedValue];
}
